import { useTranslation } from 'react-i18next';

import { Radio, RadioGroup } from '@/components/atoms/RadioGroup';
import PageLayout from '@/components/Layout/PageLayout';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import { useCage, useCageName } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { useGetTilt } from '@/domain/behavior';
import { isValidCageId } from '@/domain/cages';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';

import { TILT_THRESHOLD_DEFAULT } from '../BehaviorDashboard/BehaviorDashboardContainer';

import BehaviorTiltView from './BehaviorTiltView';
import { useTiltChartData } from './useTiltChartData';

export default function BehaviorTiltContainer() {
    const { t } = useTranslation('behaviorModule');
    const { cageId } = useCage();
    const cageName = useCageName();
    const { selectedFishGroupId } = useCageFilter();

    const { dateFrom, dateTo, onDateChange } = useDateRangePicker({
        key: 'tilt',
        defaultPresetId: 'last7days',
    });

    const query = useGetTilt(
        cageId,
        {
            from: dateFrom,
            to: dateTo,
            fishGroupId: selectedFishGroupId,
        },
        {
            enabled: isValidCageId(cageId),
        }
    );
    const { data, isLoading } = query;

    const isFetchingData = !isValidCageId(cageId) || isLoading;

    const tiltDataPoints = data?.data ?? [];
    const tiltThreshold = TILT_THRESHOLD_DEFAULT;
    const lastMeasurement = data?.lastMeasurement ?? '';

    const { chart, hasOxygenData, selectedRightAxisValue, setSelectedRightAxisValue } =
        useTiltChartData({
            tiltDataPoints,
            tiltThreshold,
        });

    const legendRight = (
        <RadioGroup
            direction="horizontal"
            value={selectedRightAxisValue}
            onValueChange={setSelectedRightAxisValue}>
            <Radio value="temperature" label={t('common.temperature')} />
            <Radio value="depth" label={t('common.depth')} />
            {hasOxygenData && <Radio value="oxygen" label={t('common.oxygen')} />}
        </RadioGroup>
    );

    return (
        <PageLayout title={cageName} subtitle={t('tiltView.title')}>
            <BehaviorTiltView
                chart={chart}
                tiltThreshold={tiltThreshold}
                lastMeasurement={lastMeasurement}
                isLoading={isFetchingData}
                error={query.error}
                retry={retryFailedQueries(query)}
                legendRight={legendRight}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={onDateChange}
            />
        </PageLayout>
    );
}
